import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '~/shadcn/ui/dialog'
import { Button } from '~/shadcn/ui/button/button'
import { Input } from '~/shadcn/ui/input'
import { Label } from '~/shadcn/ui/label'
import { Spinner } from '~/shadcn/ui/spinner'
import { FieldError } from '~/shadcn/ui/field'
import { parsePhoneNumberFromString } from 'libphonenumber-js/max'
import { useDebounce } from '~/@lib/utils/function.utils'
import { getApiErrorMessage } from '~/@lib/utils/error.utils'
import { slugify } from '~/@lib/utils/string.utils'
import { useLocalNotifications } from '~/@mid_ui/local-notifications/useLocalNotifications'
import { TextTooltip } from '~/@mid_ui/text-tooltip/TextTooltip'
import { GraphIcon } from '~/@mid_ui/icons/GraphIcon'
import { useAmbassadorCodeCheck } from '~/@biz_ambassador/code/useAmbassadorCodeCheck'
import { getAmbassadorLink } from '~/@biz_ambassador/code/getAmbassadorLink'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreate: (data: {
    reference: string
    phone?: string
    code: string
  }) => Promise<unknown>
}

export function CreateAmbassadorDialog({
  open,
  onOpenChange,
  onCreate,
}: Props) {
  const [reference, setReference] = useState('')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [isCodeManual, setIsCodeManual] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const { setNotification } = useLocalNotifications()
  const { debounce } = useDebounce(1000)
  const [debouncedCode, setDebouncedCode] = useState('')

  const codeCheck = useAmbassadorCodeCheck(debouncedCode)
  const codeResult = codeCheck.data
  const isCodeTaken = codeResult?.available === false
  const isCodeAvailable = codeResult?.available === true

  const link = getAmbassadorLink(code)

  function applyCode(value: string) {
    setCode(value)
    debounce(() => setDebouncedCode(value))
  }

  function handleReferenceChange(value: string) {
    setReference(value)
    // Auto-generate the code from the reference unless it was edited by hand.
    if (!isCodeManual) {
      applyCode(slugify(value))
    }
  }

  function handleCodeChange(value: string) {
    applyCode(slugify(value))
    // Once edited by hand the code stops following the reference.
    setIsCodeManual(true)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const trimmedPhone = phone.trim()
    if (
      trimmedPhone &&
      !parsePhoneNumberFromString(trimmedPhone, 'SK')?.isValid()
    ) {
      setPhoneError('Zadajte platné telefónne číslo.')
      return
    }
    setPhoneError(null)

    setError(null)
    setIsSubmitting(true)
    try {
      await onCreate({
        reference,
        phone: phone.trim() || undefined,
        code,
      })
      setReference('')
      setPhone('')
      setCode('')
      setDebouncedCode('')
      setIsCodeManual(false)
      setPhoneError(null)
      onOpenChange(false)
    } catch (err) {
      setError(getApiErrorMessage(err, 'Ambasádora sa nepodarilo vytvoriť.'))
    } finally {
      setIsSubmitting(false)
    }
  }

  async function copyLink() {
    await navigator.clipboard?.writeText(link)
    setNotification('success', 'Odkaz bol skopírovaný.')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(600px,calc(100%-2rem))] w-full max-h-[90svh] gap-10 px-6 md:px-15 pt-12 pb-13">
        <div className="flex flex-col items-center gap-5 text-center">
          <DialogTitle className="text-2xl font-semibold">
            Vytvoriť nového ambasádora
          </DialogTitle>
          <DialogDescription className="text-base font-medium text-primary">
            Zadajte základné informácie o novom ambasádorovi.
          </DialogDescription>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-10 min-w-0">
          <div className="flex flex-col gap-6 min-w-0">
            <div className="flex flex-col gap-1">
              <div className="flex flex-row justify-between">
                <Label
                  htmlFor="ambassador-reference"
                  className="text-sm font-medium text-primary">
                  Vaša referencia
                </Label>
                <TextTooltip text="Zadajte meno, názov spoločnosti, YouTube kanálu, blogu alebo iný identifikátor, pod ktorým budete zdieľať svoj referral odkaz. Klienti podľa neho spoznajú, od koho odkaz pochádza." />
              </div>
              <Input
                id="ambassador-reference"
                className="rounded shadow-none"
                value={reference}
                onChange={(e) => handleReferenceChange(e.target.value)}
                placeholder="Meno a priezvisko"
              />
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex flex-row justify-between">
                <Label
                  htmlFor="ambassador-phone"
                  className="text-sm font-medium text-primary">
                  Telefónne číslo
                </Label>
                <TextTooltip text="Toto číslo sa zobrazí klientom vo vašom profile, aby vás mohli kontaktovať v prípade otázok k investícii." />
              </div>
              <Input
                id="ambassador-phone"
                className="rounded shadow-none"
                type="tel"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value)
                  setPhoneError(null)
                }}
                placeholder="+421"
              />
              <div className="h-5">
                {phoneError && <FieldError>{phoneError}</FieldError>}
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex flex-row justify-between">
                <Label
                  htmlFor="ambassador-code"
                  className="text-sm font-medium text-primary">
                  Kód
                </Label>{' '}
                <TextTooltip text="Kód sa automaticky vygeneruje z vašej referencie, no môžete si ho upraviť. Klienti sa cez neho môžu zaregistrovať priamo cez váš referral odkaz, alebo ho zadať vo svojom profile po registrácii. Kód musí byť jedinečný." />
              </div>
              <Input
                id="ambassador-code"
                className="rounded shadow-none"
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                placeholder="Kód ambasádora"
              />
              <div className="h-5">
                {!codeCheck.isFetching && codeResult?.available === false && (
                  <FieldError>{codeResult.message}</FieldError>
                )}
                {!codeCheck.isFetching && isCodeAvailable && (
                  <span className="text-sm font-medium text-green-dark">
                    Kód je dostupný.
                  </span>
                )}
              </div>
            </div>

            <div className="flex h-17.5 items-center justify-between gap-2 rounded border-2 border-accent px-3">
              <span className="min-w-0 flex-1 truncate text-accent">
                {link}
              </span>
              <button
                type="button"
                onClick={copyLink}
                className="shrink-0 text-accent cursor-pointer">
                <GraphIcon className="text-accent shrink-0" />
              </button>
            </div>
          </div>

          {error && <FieldError className="self-center">{error}</FieldError>}

          <Button
            type="submit"
            className="self-center mt-2"
            disabled={
              !reference.trim() || !code.trim() || isCodeTaken || isSubmitting
            }>
            {isSubmitting && <Spinner className="size-5" />}
            Vytvoriť nového ambasádora
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
